// 2026-09-06 신규 — 루키 승급 심사 상태 조회 (RPC get_rookie_promotion_status, 마이그레이션 20260906150000).
// 대표 결정: 루키가 작품 영상 5편을 채우면 자동으로 「승급 심사」가 열리고, 레이블리가 영상을 확인한 뒤
// 승인하면 그날 바로 실버 확정, 반려하면 사유가 알림으로 가고 크리에이터가 보완 뒤 다시 신청한다.
// 홈 등급 카드·상단바 배지·/settings 등급 카드·/my-tier 가 쓴다(useCleanMarkStatus 와 같은 계약):
// 모듈 캐시 5분 + 진행 중 요청 합치기, 실패하면 null(화면은 종전 루키 문장 그대로 — 등급 화면이 깨지면 안 된다).
// 호출처가 `enabled` 로 루키 계열일 때만 켠다 — 정식 등급 계정은 이 RPC 를 부르지 않는다.
// p_email 은 항상 넘긴다 — 본인이면 JWT 이메일과 같아 그대로 허용되고, 어드민이 대리 접속(impersonateEmail) 중이면
// 서버가 is_admin() 으로 허용해 **그 크리에이터의** 상태를 보여준다. 어드민이 아닌 계정이 남의 이메일을 넣으면 42501.
import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { addTierRowsRevisit, createTierRowsWatch, noteTierAttempt, onTierRowsReset } from "../lib/tierRowsSignal";

const TTL_MS = 5 * 60 * 1000;
let cache = null; // { email, at, status }
let inflight = null; // { email, promise }
// 신호(채널 쓰기 · 탭 복귀) 때 캐시를 바로 비운다 — 등급 요약 캐시와 같은 방식(신호 모듈의 비우기 등록). 예전에는 듣는 훅이 다음 그리기에서 비워서
// 훅이 하나도 떠 있지 않을 때(운영 화면에서 대리 접속 회원의 채널을 고침) 온 신호를 놓치고 옛 상태를 5분 동안 내줬다(96차 #1·#6)
let signalSeq = 0;   // 등급 신호 번호 — 진행 중 요청이 「마지막 신호 뒤에 시작했나」를 refresh() 가 본다(110차 #5)
onTierRowsReset(() => {
  cache = null;
  inflight = null;
  signalSeq += 1;
});
const refreshListeners = new Set(); // refresh() 를 떠 있는 모든 카드에 알린다(94차 #2)
// 탭 복귀 때 이 캐시가 30초보다 오래됐으면 다시 읽게 — 요약 훅이 없는 화면에서도(판정은 캐시를 가진 모듈마다 등록 · 94차 #4)
// 지금 그 회원을 그리는 켜진 훅이 있을 때만 — 승인돼 훅이 꺼졌거나 대리 접속을 마친 옛 회원의 캐시가 탭마다 신호를 보내지 않게(95차 #1)
const statusWatch = createTierRowsWatch();
// 회원마다 마지막으로 서버에 물은 시각 — 캐시는 한 칸이라 다른 회원의 늦은 응답이 지금 회원의 캐시를 밀어낼 수 있다(대리 접속 전환 · 100차 #1).
// 지금 회원의 캐시가 없으면 「마지막으로 물은 지 30초」로 다시 읽는다(실패 뒤 다시 시도 · 97차 #4 와 같은 규칙)
const lastAttemptAt = new Map();
// 탭 복귀 규칙은 신호 모듈 한 곳(addTierRowsRevisit · 104차 #5) — 캐시는 한 칸이라 그 회원 것일 때만 캐시로 본다
addTierRowsRevisit(statusWatch, {
  isInflight: (e) => Boolean(inflight && inflight.email === e),
  cacheAt: (e) => (cache && cache.email === e ? cache.at : undefined),
  lastAttemptAt: (e) => lastAttemptAt.get(e),
  dropCache: (e) => { if (cache && cache.email === e) cache = null; },
});

export function _resetRookiePromotionCache() {
  cache = null;
  inflight = null;
  lastAttemptAt.clear();
  refreshListeners.clear();
  signalSeq = 0;
}

// 서버 jsonb(snake_case) → 화면 객체(camelCase). 모르는 값은 기본값으로 — 화면이 undefined 를 보지 않게.
export function normalizeRookiePromotionStatus(raw) {
  if (!raw || typeof raw !== "object") return null;
  const r = raw.review && typeof raw.review === "object" ? raw.review : null;
  return {
    enabled: Boolean(raw.enabled),
    practiceOnly: Boolean(raw.practice_only),
    // 시행일(app_config.rookie_practice_from, "YYYY-MM-DD"). 그 전 업로드는 작품과 무관하게 센다. 없으면 null.
    practiceFrom: raw.practice_from ?? null,
    // 2026-09-22 배정 — 이 사람에게 루키 작품 랜덤 배정이 걸리는가(스위치 + 대상 + 아직 정식 등급 아님). 홈이 useRookieAssignment 를 켤지 정한다.
    assignmentEnabled: Boolean(raw.assignment_enabled),
    assignmentSize: Number(raw.assignment_size) || 5,
    // 2026-09-29 핀(협업 코드 가입자) — 배정이 고정이면 true. 문구만 바뀌고 판정은 서버가 한다
    assignmentPinned: Boolean(raw.assignment_pinned),
    assignmentDistinct: Boolean(raw.assignment_distinct),
    review: r
      ? {
          id: r.id ?? null,
          status: r.status ?? null,
          requestedAt: r.requested_at ?? null,
          reviewedAt: r.reviewed_at ?? null,
          creatorMessage: r.creator_message ?? null,
          debutMonth: r.debut_month ?? null,
          videoCount: r.video_count ?? 0,
        }
      : null,
    eligible: Boolean(raw.eligible),
    // 서버가 진행도를 주지 않으면(일별 행 없음 · 옛 서버) null — 0 으로 바꾸면 withRookieReview 가 요약 훅의 진행도(일별·월 행)를 0 으로 덮어
    // 3편 올린 회원에게 「작품 하나 골라 신청부터」가 떴다(123차 #3·#4). 쓰는 곳은 null 을 「모름」으로 다룬다 — 홈 진행도 한 곳(rookieProg)은 `??` 로
    // 요약 훅 값에 넘기고, 축하 시트는 null 이면 막지 않는다(126차 #5). `|| 0` 으로 받지 말 것(149차 #6)
    progressCurrent: raw.progress_current === null || raw.progress_current === undefined || raw.progress_current === "" || !Number.isFinite(Number(raw.progress_current))
      ? null : Number(raw.progress_current),
    progressTarget: Number(raw.progress_target) || 5,
    // 2026-10-02 「루키로 남기」(대표 결정) — true 면 5/5 여도 심사가 열리지 않고 루키로 남는다(배정 작품 계속). 구버전 서버(키 없음)는 false
    stay: Boolean(raw.stay),
    stayChosenAt: raw.stay_chosen_at ?? null,
    // 2026-10-10 — 심사 제도 스위치(전체 · rookie_review_enabled()). 자동 종료·재개설은 이 값만 본다 — 위 enabled 는 회원 규칙까지 곱한 값(78차 #2).
    // 옛 서버(키 없음)는 null — 화면은 모른다고 보고 서버 문장 그대로
    reviewSwitch: typeof raw.review_switch === "boolean" ? raw.review_switch : null,
  };
}

async function fetchStatus(email) {
  if (cache && cache.email === email && Date.now() - cache.at < TTL_MS) return cache.status;
  if (inflight && inflight.email === email) return inflight.promise;
  noteTierAttempt(lastAttemptAt, statusWatch, email);
  const promise = (async () => {
    const { data, error } = await supabase.rpc("get_rookie_promotion_status", { p_email: email });
    if (error) throw error;
    const status = normalizeRookiePromotionStatus(data);
    // 신호(등급 캐시 무효화)로 버린 요청이면 캐시에 쓰지 않는다 — 옛 상태가 5분 동안 되살아나지 않게(90차 #1)
    if (inflight && inflight.promise === promise) {
      cache = { email, at: Date.now(), status };
      inflight = null;
    }
    return status;
  })().catch((err) => {
    if (inflight && inflight.promise === promise) inflight = null;
    throw err;
  });
  inflight = { email, promise, seq: signalSeq };
  return promise;
}

/**
 * @param {string|undefined} email
 * @param {{ enabled?: boolean }} [options] 루키 계열(summary.status === "rookie" 등)일 때만 true 로.
 * @returns {{ loading: boolean, status: object|null, refresh: () => Promise<void> }}
 *   refresh 는 캐시를 무시하고 다시 읽는다 — 재신청 직후 「심사 중」으로 바로 바꾸려고.
 */
export default function useRookiePromotionStatus(email, { enabled = true } = {}) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(Boolean(enabled && email));
  // 등급 캐시 무효화(채널 쓰기 · 탭 복귀)와 같은 신호로 다시 읽는다 — 다른 탭·대기열 크론이 심사를 자동 종료했는데 카드가 5분 동안
  // 「승급 심사 중」으로 남지 않게(90차 #1). 이 다시 읽기는 조용히(그려 둔 카드를 로딩으로 지우지 않는다). 꺼진 훅(정식 등급 회원)은 듣지 않는다(94차 #3)
  const [gen, setGen] = useState(0);
  const active = Boolean(enabled && email);
  useEffect(() => (active ? statusWatch.watch(email, setGen) : undefined), [active, email]);
  const shownFor = useRef(null);
  // 이 카드가 마지막으로 시작한 읽기 번호 — 늦게 끝난 옛 읽기(새로고침 · 신호)가 새 결과를 덮지 않게(97차 #1)
  const reqSeq = useRef(0);

  useEffect(() => {
    if (!enabled || !email) {
      setStatus(null);
      setLoading(false);
      shownFor.current = null;
      return undefined;
    }
    let cancelled = false;
    const silent = shownFor.current === email;
    if (!silent) setLoading(true);
    const seq = ++reqSeq.current;
    const latest = () => !cancelled && seq === reqSeq.current;
    fetchStatus(email)
      .then((s) => {
        if (latest()) {
          setStatus(s);
          shownFor.current = email;
        }
      })
      .catch((err) => {
        if (latest()) {
          console.warn("승급 심사 상태 조회 실패:", err?.message || err);
          if (!silent) setStatus(null);   // 조용한 다시 읽기가 실패하면 그려 둔 카드를 지우지 않는다
        }
      })
      .finally(() => {
        if (latest()) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [email, enabled, gen]);

  // refresh() — 「다시 신청」·「루키로 남기」 직후. 떠 있는 **모든** 카드(상단바·홈·내 등급)가 같은 새 요청을 받아 로딩을 보이고,
  // 실패하면 옛 상태(반려 · 「다시 신청」 버튼)를 지운다(93차 #2 · 94차 #2). 부른 쪽은 새 상태를 받을 때까지 기다린다(94차 #1 — 버튼이 옛 상태에서 다시 눌리지 않게)
  useEffect(() => {
    if (!active) return undefined;
    const onRefresh = (who, promise) => {
      if (who !== email) return;
      setLoading(true);
      const seq = ++reqSeq.current;
      const latest = () => seq === reqSeq.current;
      promise
        .then((s) => {
          if (!latest()) return;
          setStatus(s);
          shownFor.current = email;
        })
        .catch((err) => {
          if (!latest()) return;
          console.warn("승급 심사 상태 조회 실패:", err?.message || err);
          setStatus(null);
        })
        .finally(() => {
          if (latest()) setLoading(false);
        });
    };
    refreshListeners.add(onRefresh);
    return () => {
      refreshListeners.delete(onRefresh);
    };
  }, [active, email]);

  const refresh = useCallback(async () => {
    if (!email) return null;
    cache = null;
    // 재신청 전에 시작한 요청을 이어받으면 옛 상태(반려)가 돌아온다 — 버린다. 단 마지막 등급 신호(재신청·루키로 남기는 tierRpc 로 보낸다) **뒤에**
    // 시작한 요청은 이미 새 상태를 묻는 중이라 이어받는다(신호를 들은 카드가 먼저 다시 읽기 시작했으면 같은 것을 두 번 묻지 않게 · 110차 #5)
    if (!(inflight && inflight.email === email && inflight.seq === signalSeq && signalSeq > 0)) inflight = null;
    const promise = fetchStatus(email);
    for (const listener of [...refreshListeners]) listener(email, promise);
    return promise.catch(() => null);
  }, [email]);

  return { status, loading, refresh };
}
